// ─── AccountSwitcher ──────────────────────────────────────────────────────────
// Gmail-style multi-account dropdown. Shows all loaded accounts, lets you
// switch between them, add a new one, or sign out.

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { LogOut, Plus, Check, X, Loader2, User as UserIcon, Palette, LayoutDashboard } from 'lucide-react'
import { useAccountStore } from '../../stores/accountStore'
import { useMailStore } from '../../stores/mailStore'
import { auth as authApi, clearAccessToken, setAccessToken } from '../../lib/api'
import type { User } from '../../lib/types'

// ─── Props ────────────────────────────────────────────────────────────────────

interface AccountSwitcherProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onOpenProfile?: () => void
  onOpenCustomizer?: () => void
  isAdmin?: boolean
}

// ─── Add-account mini login form ──────────────────────────────────────────────

interface AddAccountFormProps {
  onSuccess: () => void
  onCancel: () => void
}

function AddAccountForm({ onSuccess, onCancel }: AddAccountFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { addAccount, getPersistedAccounts } = useAccountStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const resp = await authApi.login({ email, password })
      await addAccount(
        resp.user.email,
        resp.access_token,
        resp.refresh_token ?? '',
        resp.jmap_url ?? '',
        resp.jmap_password ?? '',
        resp.user as User,
      )
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="account-switcher__add-form">
      <div className="account-switcher__add-title">Add account</div>
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        className="account-switcher__input"
        autoFocus
        required
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        className="account-switcher__input"
        required
      />
      {error && <div className="account-switcher__error">{error}</div>}
      <div className="account-switcher__add-actions">
        <button type="button" onClick={onCancel} className="account-switcher__btn account-switcher__btn--ghost">
          Cancel
        </button>
        <button type="submit" disabled={loading} className="account-switcher__btn account-switcher__btn--primary">
          {loading ? <Loader2 size={14} className="spin" /> : 'Sign in'}
        </button>
      </div>
    </form>
  )
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ email, name, size = 36 }: { email: string; name?: string; size?: number }) {
  const char = (name || email)[0].toUpperCase()
  const hue = [...email].reduce((h, c) => h + c.charCodeAt(0), 0) % 360
  return (
    <div
      className="account-switcher__avatar"
      style={{
        width: size, height: size,
        background: `hsl(${hue}, 55%, 48%)`,
        fontSize: size * 0.42,
      }}
    >
      {char}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AccountSwitcher({ anchorEl, open, onClose, onOpenProfile, onOpenCustomizer, isAdmin }: AccountSwitcherProps) {
  const { accounts, activeEmail, setActiveAccount, removeAccount, updateAccount } = useAccountStore()
  const { setUser } = useMailStore()
  const [showAdd, setShowAdd] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) { setShowAdd(false); return }
    const onMouseDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open || !anchorEl) return null

  const rect = anchorEl.getBoundingClientRect()
  const style: React.CSSProperties = {
    position: 'fixed',
    top: rect.bottom + 6,
    right: Math.max(8, window.innerWidth - rect.right),
    width: 320,
    zIndex: 9500,
  }

  const handleSwitch = async (email: string) => {
    const account = accounts.find(a => a.email === email)
    if (!account) return
    setActiveAccount(email)
    // Optimistic update with whatever we have cached
    if (account.user) setUser(account.user)
    setAccessToken(account.accessToken)

    // Fetch /auth/me with the account's OWN token to pick up DB role changes
    // (e.g. a just-promoted superadmin) without a page reload.
    //
    // IMPORTANT: do NOT use credentials:'include' here — the httpOnly cookie
    // belongs to the PRIMARY login account, not the switched-to account. Sending
    // it to /auth/refresh would return the WRONG user's token.
    const apiBase = (import.meta.env.VITE_API_URL ?? '').replace(/\/api\/?$/, '')
    const isExpired = (t: string) => {
      try { const p = JSON.parse(atob(t.split('.')[1])); return p.exp * 1000 < Date.now() } catch { return true }
    }
    try {
      let token = account.accessToken
      if (isExpired(token)) {
        // Only try to refresh if we have a dedicated body RT for this account.
        // Send WITHOUT credentials:'include' so the primary account's cookie
        // doesn't override the body token.
        if (!account.refreshToken) throw new Error('no-rt')
        const r = await fetch(`${apiBase}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: account.refreshToken }),
          // deliberately no credentials:'include'
        })
        if (!r.ok) throw new Error('refresh-failed')
        const { access_token } = await r.json()
        token = access_token
        setAccessToken(access_token)
        updateAccount(email, { accessToken: access_token })
      }
      // /auth/me reads from DB, so it always reflects the current role even if the
      // JWT was issued before a role change. Use the account's own token, no cookie.
      const meRes = await fetch(`${apiBase}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (meRes.ok) {
        const freshUser = await meRes.json()
        // Sanity check: make sure /auth/me returned data for THIS account
        if (freshUser.email === email) {
          updateAccount(email, { user: freshUser, cachedRole: freshUser.role, cachedName: freshUser.name })
          setUser(freshUser)
        }
      }
    } catch { /* best-effort — cached data already applied above */ }

    // Trigger full re-fetch for the new active account
    window.dispatchEvent(new CustomEvent('account:switched', { detail: { email } }))
    onClose()
  }

  const handleSignOut = (email: string, e: React.MouseEvent) => {
    e.stopPropagation()
    removeAccount(email)
    if (email === activeEmail) {
      clearAccessToken()
      window.location.reload()
    }
  }

  const handleSignOutAll = () => {
    for (const acc of accounts) removeAccount(acc.email)
    clearAccessToken()
    window.location.reload()
  }

  const activeAccount = accounts.find(a => a.email === activeEmail)

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          className="account-switcher"
          style={style}
          initial={{ opacity: 0, y: -8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Active account header */}
          {activeAccount && (
            <div className="account-switcher__header">
              <Avatar email={activeAccount.email} name={activeAccount.user?.name} size={48} />
              <div className="account-switcher__header-info">
                <div className="account-switcher__header-name">
                  Hi, {activeAccount.user?.name?.split(' ')[0] ?? 'there'}!
                </div>
                <div className="account-switcher__header-email">{activeAccount.email}</div>
              </div>
            </div>
          )}

          <div className="account-switcher__divider" />

          {/* Account list */}
          <div className="account-switcher__list">
            {accounts.map(account => (
              <div
                key={account.email}
                className={`account-switcher__item ${account.email === activeEmail ? 'account-switcher__item--active' : ''}`}
                onClick={() => handleSwitch(account.email)}
              >
                <Avatar email={account.email} name={account.user?.name} />
                <div className="account-switcher__item-info">
                  <div className="account-switcher__item-name">
                    {account.user?.name ?? account.email}
                  </div>
                  <div className="account-switcher__item-email">{account.email}</div>
                </div>
                <div className="account-switcher__item-actions">
                  {account.email === activeEmail && (
                    <Check size={14} className="account-switcher__item-check" />
                  )}
                  {account.isLoading && (
                    <Loader2 size={14} className="spin" />
                  )}
                  {!account.isLoading && (
                    <button
                      className="account-switcher__item-remove"
                      onClick={e => handleSignOut(account.email, e)}
                      title="Sign out"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="account-switcher__divider" />

          {/* Profile / Settings links */}
          {(onOpenProfile || onOpenCustomizer) && (
            <>
              <div className="account-switcher__actions">
                {onOpenProfile && (
                  <button className="account-switcher__action-btn" onClick={() => { onOpenProfile(); onClose() }}>
                    <UserIcon size={16} />
                    <span>Profile &amp; Settings</span>
                  </button>
                )}
                {onOpenCustomizer && (
                  <button className="account-switcher__action-btn" onClick={() => { onOpenCustomizer(); onClose() }}>
                    <Palette size={16} />
                    <span>Theme Studio</span>
                  </button>
                )}
                {isAdmin && (
                  <button className="account-switcher__action-btn account-switcher__action-btn--highlight" onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-cp'))
                    onClose()
                  }}>
                    <LayoutDashboard size={16} />
                    <span>Control Panel</span>
                  </button>
                )}
              </div>
              <div className="account-switcher__divider" />
            </>
          )}

          {/* Add account / sign out */}
          {showAdd ? (
            <AddAccountForm
              onSuccess={() => setShowAdd(false)}
              onCancel={() => setShowAdd(false)}
            />
          ) : (
            <div className="account-switcher__actions">
              {accounts.length < 5 && (
                <button className="account-switcher__action-btn" onClick={() => setShowAdd(true)}>
                  <Plus size={16} />
                  <span>Add another account</span>
                </button>
              )}
              <button className="account-switcher__action-btn account-switcher__action-btn--danger" onClick={handleSignOutAll}>
                <LogOut size={16} />
                <span>Sign out of all accounts</span>
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
